import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrandedHeader } from './BrandedHeader';
import { ChatInput } from './ChatInput';
import { MessageBubble, Message } from './MessageBubble';
import { motion, AnimatePresence } from 'motion/react';

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  messages: Message[];
  onSendMessage: (text: string) => void;
  isTyping?: boolean;
  connectionStatus?: string;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  onClose,
  messages,
  onSendMessage,
  isTyping,
  connectionStatus,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const [isMobile, setIsMobile] = useState(false);

  // --- Issue 6: Synchronized message queue ---
  // Only messages whose IDs are in this set are rendered/visible
  const [releasedIds, setReleasedIds] = useState<Set<string>>(new Set());
  const streamingQueue = useRef<string[]>([]);   // IDs waiting their turn
  const isAnimating = useRef(false);             // true while a bubble is typing

  const releaseNext = useCallback(() => {
    const nextId = streamingQueue.current.shift();
    if (nextId) {
      isAnimating.current = true;
      setReleasedIds(prev => new Set([...prev, nextId]));
    } else {
      isAnimating.current = false;
    }
  }, []);

  const prevIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    messages.forEach(msg => {
      if (prevIdsRef.current.has(msg.id)) return;
      prevIdsRef.current.add(msg.id);

      if (msg.sender === 'user' || msg.isTyping || !msg.isStreaming) {
        // User messages and non-streaming bot messages show immediately
        setReleasedIds(prev => new Set([...prev, msg.id]));
      } else {
        // Streaming bot messages: queue and release one at a time
        streamingQueue.current.push(msg.id);
        if (!isAnimating.current) releaseNext();
      }
    });
  }, [messages, releaseNext]);

  const handleMessageComplete = useCallback((id: string) => {
    void id; // id kept for debugging if needed
    releaseNext();
  }, [releaseNext]);

  // When chat opens, reset queue state for a clean start
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      isAnimating.current = false;
      streamingQueue.current = [];
      prevIdsRef.current = new Set();

      // All messages that already exist when the widget opens should show
      // immediately — no streaming animation needed since they arrived
      // while the widget was closed (e.g. the greeting on first load).
      const allIds = new Set(messages.map(msg => msg.id));
      messages.forEach(msg => prevIdsRef.current.add(msg.id));
      setReleasedIds(allIds);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, messages]);
  // --- end queue logic ---

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track whether user is near the bottom so we don't hijack manual scroll-up
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  // ResizeObserver: scroll to bottom whenever content height grows (streaming text, new bubbles, typing indicator)
  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;
    const observer = new ResizeObserver(() => {
      if (isNearBottomRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    });
    observer.observe(contentEl);
    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className={`fixed z-50 bg-white shadow-2xl overflow-hidden flex flex-col ring-4 ring-[#0B3D91]/20 ${isMobile
            ? 'bottom-0 left-0 w-full h-full rounded-none top-0'
            : 'bottom-6 right-6 w-[400px] h-[650px] rounded-2xl border-2 border-[#0B3D91]/30'
            }`}
        >
          <BrandedHeader
            onClose={onClose}
            onMinimize={isMobile ? undefined : onClose}
          />

          {/* Connection status banner */}
          {(connectionStatus === 'offline' || connectionStatus === 'reconnecting') && (
            <div className="px-4 py-2 text-center text-xs text-white bg-yellow-500 flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
              {connectionStatus === 'reconnecting' ? 'Reconnecting…' : 'Disconnected – waiting for server…'}
            </div>
          )}

          {/* Messages Area */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto bg-gradient-to-b from-[#EFF3F9] to-[#F5F7FB]"
          >
            <div ref={contentRef} className="p-5 space-y-1">
              {messages
                .filter(msg => releasedIds.has(msg.id))
                .map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    onComplete={msg.isStreaming ? () => handleMessageComplete(msg.id) : undefined}
                  />
                ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#0B3D91]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm inline-block">
                    <div className="flex space-x-1 items-center h-4">
                      <motion.div
                        className="w-1.5 h-1.5 bg-[#0B3D91]/40 rounded-full"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                      />
                      <motion.div
                        className="w-1.5 h-1.5 bg-[#0B3D91]/40 rounded-full"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                      />
                      <motion.div
                        className="w-1.5 h-1.5 bg-[#0B3D91]/40 rounded-full"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area - Sticky at bottom */}
          <ChatInput
            onSend={onSendMessage}
            quickReplies={["Mainframe courses", "Trainer reviews", "Fees & batches", "Job support"]}
            onQuickReplyClick={onSendMessage}
            disabled={connectionStatus === 'offline' || connectionStatus === 'reconnecting'}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};